"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type RingtoneSoundOption =
  | "none"
  | "chime"
  | "pulse"
  | "melody"
  | "ripple"
  | "beacon";

const STORAGE_KEY = "ringtone-sound-preference";

export const RINGTONE_OPTIONS: {
  value: RingtoneSoundOption;
  label: string;
  description: string;
}[] = [
  { value: "none", label: "None", description: "No ringtone" },
  { value: "chime", label: "Chime", description: "Two-note descending bell" },
  { value: "pulse", label: "Pulse", description: "Soft evenly-spaced pulses" },
  {
    value: "melody",
    label: "Melody",
    description: "Ascending/descending arpeggio",
  },
  {
    value: "ripple",
    label: "Ripple",
    description: "Cascading pentatonic descent",
  },
  { value: "beacon", label: "Beacon", description: "Modern two-tone signal" },
];

function createAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    return new (window.AudioContext || (window as any).webkitAudioContext)();
  } catch {
    return null;
  }
}

/** Schedule a single filtered, enveloped bell-like note. */
function createNote(ctx: AudioContext, freq: number, startTime: number) {
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 2500;
  filter.connect(ctx.destination);

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.frequency.value = freq;
  osc.type = "sine";
  osc.connect(gain);
  gain.connect(filter);

  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(0.22, startTime + 0.01);
  gain.gain.setValueAtTime(0.22, startTime + 0.16);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.56);

  osc.start(startTime);
  osc.stop(startTime + 0.56);

  const harm = ctx.createOscillator();
  const harmGain = ctx.createGain();
  harm.frequency.value = freq * 2;
  harm.type = "sine";
  harm.connect(harmGain);
  harmGain.connect(filter);

  harmGain.gain.setValueAtTime(0, startTime);
  harmGain.gain.linearRampToValueAtTime(0.033, startTime + 0.01);
  harmGain.gain.setValueAtTime(0.033, startTime + 0.16);
  harmGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.56);

  harm.start(startTime);
  harm.stop(startTime + 0.56);
}

/** Chime: Two-note descending bell (E5→A4), played twice. ~1.3s */
function playChime(ctx: AudioContext) {
  const now = ctx.currentTime;
  const E5 = 659.25;
  const A4 = 440;

  createNote(ctx, E5, now);
  createNote(ctx, A4, now + 0.3);
  createNote(ctx, E5, now + 0.65);
  createNote(ctx, A4, now + 0.95);
}

/** Pulse: Three soft evenly-spaced pulses with sub-oscillator warmth. ~1.0s */
function playPulse(ctx: AudioContext) {
  const now = ctx.currentTime;
  const A4 = 440;

  for (let i = 0; i < 3; i++) {
    const t = now + i * 0.35;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = A4;
    osc.type = "sine";
    filter.type = "lowpass";
    filter.frequency.value = 2500;

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.2, t + 0.01);
    gain.gain.setValueAtTime(0.2, t + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);

    osc.start(t);
    osc.stop(t + 0.28);

    // Sub-oscillator for warmth
    const sub = ctx.createOscillator();
    const subGain = ctx.createGain();
    sub.connect(subGain);
    subGain.connect(ctx.destination);

    sub.frequency.value = 220;
    sub.type = "sine";

    subGain.gain.setValueAtTime(0, t);
    subGain.gain.linearRampToValueAtTime(0.08, t + 0.01);
    subGain.gain.setValueAtTime(0.08, t + 0.1);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);

    sub.start(t);
    sub.stop(t + 0.28);
  }
}

/** Melody: 4-note C major arpeggio C5→E5→G5→E5, 0.25s spacing. ~1.2s */
function playMelody(ctx: AudioContext) {
  const now = ctx.currentTime;
  const notes = [523.25, 659.25, 783.99, 659.25]; // C5, E5, G5, E5

  notes.forEach((freq, i) => {
    const t = now + i * 0.25;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = freq;
    osc.type = "sine";
    filter.type = "lowpass";
    filter.frequency.value = 3000;

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.2, t + 0.01);
    gain.gain.setValueAtTime(0.2, t + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);

    osc.start(t);
    osc.stop(t + 0.4);
  });
}

/** Ripple: Fast cascading pentatonic descent B5→A5→F#5→E5→D5, 0.1s spacing. ~1.0s */
function playRipple(ctx: AudioContext) {
  const now = ctx.currentTime;
  const notes = [987.77, 880, 739.99, 659.25, 587.33]; // B5, A5, F#5, E5, D5

  notes.forEach((freq, i) => {
    const t = now + i * 0.1;
    const volume = 0.22 - i * 0.03;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = freq;
    osc.type = "sine";
    filter.type = "lowpass";
    filter.frequency.value = 3000;

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(volume, t + 0.01);
    gain.gain.setValueAtTime(volume, t + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

    osc.start(t);
    osc.stop(t + 0.5);
  });
}

/** Beacon: Modern two-tone signal E5→B5, played twice. ~1.1s */
function playBeacon(ctx: AudioContext) {
  const now = ctx.currentTime;
  const E5 = 659.25;
  const B5 = 987.77;
  const pairs = [
    [0, 0.15],
    [0.5, 0.65],
  ];

  pairs.forEach(([t1, t2]) => {
    [
      { freq: E5, start: now + t1 },
      { freq: B5, start: now + t2 },
    ].forEach(({ freq, start }) => {
      // Sine layer
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.value = freq;
      osc.type = "sine";
      filter.type = "bandpass";
      filter.frequency.value = freq;
      filter.Q.value = 2;

      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.18, start + 0.01);
      gain.gain.setValueAtTime(0.18, start + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);

      osc.start(start);
      osc.stop(start + 0.3);

      // Square blend layer: sq → sqGain → filter (→ gain → destination)
      const sq = ctx.createOscillator();
      const sqGain = ctx.createGain();
      sq.connect(sqGain);
      sqGain.connect(filter);

      sq.frequency.value = freq;
      sq.type = "square";

      sqGain.gain.setValueAtTime(0, start);
      sqGain.gain.linearRampToValueAtTime(0.03, start + 0.01);
      sqGain.gain.setValueAtTime(0.03, start + 0.1);
      sqGain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);

      sq.start(start);
      sq.stop(start + 0.3);
    });
  });
}

const RINGTONE_PLAYERS: Record<
  Exclude<RingtoneSoundOption, "none">,
  (ctx: AudioContext) => void
> = {
  chime: playChime,
  pulse: playPulse,
  melody: playMelody,
  ripple: playRipple,
  beacon: playBeacon,
};

const BURST_DURATION = 1.3; // seconds of audible ring
const SILENCE_GAP = 3000; // ms between bursts

/**
 * Hook for managing ringtone preference in settings UI.
 * Mirrors `useNotificationSound` pattern.
 */
export function useRingtonePreference() {
  const [ringtonePreference, setRingtonePreference] =
    useState<RingtoneSoundOption>("chime");
  const [isLoaded, setIsLoaded] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && RINGTONE_OPTIONS.some((opt) => opt.value === stored)) {
      setRingtonePreference(stored as RingtoneSoundOption);
    }
    setIsLoaded(true);

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext();
    }
    if (audioContextRef.current?.state === "suspended") {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const updateRingtonePreference = useCallback((value: RingtoneSoundOption) => {
    setRingtonePreference(value);
    localStorage.setItem(STORAGE_KEY, value);
  }, []);

  const previewRingtone = useCallback(
    (type: RingtoneSoundOption) => {
      if (type === "none") return;

      const audioContext = getAudioContext();
      if (!audioContext) return;

      try {
        RINGTONE_PLAYERS[type](audioContext);
      } catch (error) {
        console.error("Error playing ringtone preview:", error);
      }
    },
    [getAudioContext],
  );

  return {
    ringtonePreference,
    updateRingtonePreference,
    previewRingtone,
    isLoaded,
  };
}

/**
 * Plays the user's selected ringtone while `isRinging` is true.
 * Stops and cleans up when `isRinging` becomes false.
 */
export function useRingtone(isRinging: boolean): void {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isRinging) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    const preference = (
      stored && RINGTONE_OPTIONS.some((opt) => opt.value === stored)
        ? stored
        : "chime"
    ) as RingtoneSoundOption;

    if (preference === "none") return;

    const ctx = createAudioContext();
    if (!ctx) return;
    // Resume context in case browser started it in suspended state (autoplay policy)
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    ctxRef.current = ctx;

    let stopped = false;
    const player = RINGTONE_PLAYERS[preference];

    const loop = () => {
      if (stopped) return;
      try {
        player(ctx);
      } catch {
        return;
      }
      timerRef.current = setTimeout(
        loop,
        (BURST_DURATION + SILENCE_GAP / 1000) * 1000,
      );
    };

    loop();

    return () => {
      stopped = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      ctx.close();
      ctxRef.current = null;
    };
  }, [isRinging]);
}
